/**
 * Credify NotePilot, built-in drafter (calendar#46 step 5, PROGRESS_NOTE_A-15).
 *
 * Pure functions, no DOM and no network: the clinician's session summary plus the
 * structured fields already on the note become a draft per section, and a section can
 * be restyled (Formal / Clinical / In-depth). Nothing here invents facts: every
 * sentence comes from the summary or the note's own fields. A model-backed drafter
 * (a server proxy, after a BAA) can replace draftNoteSections / restyleText later
 * with the same input and output shapes.
 */

export interface NpGoal {
  goal: string;
  objective: string;
  checked: boolean;
  progress: string;
}
export interface NpContext {
  /** e.g. "50-minute individual session" */
  session: string;
  goals: NpGoal[];
  concerns: string[];
  interventions: string[];
  nextSteps: string[];
  response: string;
  approaches: string[];
  diagnoses: { code: string; label: string }[];
  /**
   * ehr#688 G5: the client's name, so the strict drafter can read "<name> said X" as
   * "Client reported X" and prefer a line about this client. The default drafter ignores it.
   */
  clientName?: string;
}
/**
 * ehr#688 G5: drafting options. `strict` applies Jay's group-note rules (GROUP_NOTE_10-29-V25
 * V21 22a, V22, V23): no stock or position-based lines, Plan only from a sentence about what comes
 * next, readable lines. Only the group path passes it today (ehr#688 question 3 decides whether
 * every note follows).
 */
export interface NpOptions {
  strict?: boolean;
}
export type NpDrafts = Record<
  | 'subjective'
  | 'objective'
  | 'assessment'
  | 'plan'
  | 'data'
  | 'behavior'
  | 'intervention'
  | 'response',
  string
>;
export type NpTone = 'formal' | 'clinical' | 'indepth';
export const NP_TONES: [NpTone, string][] = [
  ['formal', 'Formal'],
  ['clinical', 'Clinical'],
  ['indepth', 'In-depth'],
];

/** Which note fields each template lets NotePilot draft into. */
export const DRAFT_FIELDS: Record<string, (keyof NpDrafts)[]> = {
  SOAP: ['subjective', 'objective', 'assessment', 'plan'],
  DAP: ['data', 'assessment', 'plan'],
  BIRP: ['behavior', 'intervention', 'response', 'plan'],
  CREDIFY: ['objective', 'assessment'],
};

// Cue words that tag a summary sentence with a note section. Whole-word match.
const NP_CUES: Record<string, string[]> = {
  subjective: [
    'reported',
    'reports',
    'said',
    'says',
    'stated',
    'states',
    'described',
    'describes',
    'shared',
    'shares',
    'feels',
    'feeling',
    'feelings',
    'felt',
    'denied',
    'denies',
    'endorsed',
    'endorses',
    'complained',
    'complains',
    'expressed',
    'expresses',
    'mentioned',
    'mentions',
    'told',
    'worried',
    'worries',
    'wants',
    'would like',
    'in their words',
    'frustrated',
    'upset',
    'rated',
    'rates',
    'noticed',
    'notices',
    'admits',
    'admitted',
    'acknowledged',
    'acknowledges',
    'requested',
    'asked',
    'thoughts',
    'believes',
    'thinks',
    'has been',
    'since last session',
    'this week',
    'past week',
    'over the week',
  ],
  objective: [
    'appeared',
    'appears',
    'presented',
    'presents',
    'presenting',
    'presentation',
    'affect',
    'mood',
    'tearful',
    'crying',
    'eye contact',
    'oriented',
    'alert',
    'speech',
    'psychomotor',
    'restless',
    'fidgeting',
    'calm',
    'groomed',
    'disheveled',
    'on time',
    'late',
    'arrived',
    'observed',
    'noted',
    'visibly',
    'seemed',
    'seems',
    'looked',
    'flat',
    'guarded',
    'agitated',
    'cooperative',
    'attentive',
    'smiled',
    'laughed',
    'posture',
    'appearance',
    'hygiene',
    'irritable',
    'anxious',
    'depressed',
    'euthymic',
    'labile',
    'congruent',
    'dressed',
    'pressured',
    'soft-spoken',
    'withdrawn',
  ],
  intervention: [
    'practiced',
    'introduced',
    'reviewed',
    'taught',
    'used',
    'utilized',
    'applied',
    'explored',
    'processed',
    'psychoeducation',
    'role-play',
    'role play',
    'worksheet',
    'challenged',
    'discussed',
    'guided',
    'coached',
    'walked through',
    'modeled',
    'reframing',
    'reframe',
    'breathing',
    'grounding',
    'mindfulness',
    'exposure',
    'activation',
    'thought record',
    'cognitive',
    'cbt',
    'dbt',
    'emdr',
    'skill',
    'skills',
    'validated',
    'encouraged',
    'normalized',
    'worked on',
    'we',
    'clinician',
    'therapist',
    'focused on',
    'facilitated',
    'provided',
    'offered',
    'suggested',
    'recommended',
    'exercise',
    'technique',
    'strategy',
    'strategies',
    'coping',
    'relaxation',
    'boundaries',
    'values',
    'goal setting',
    'safety plan',
  ],
  response: [
    'responded',
    'receptive',
    'open to',
    'engaged',
    'resistant',
    'reluctant',
    'hesitant',
    'able to',
    'was able',
    'struggled',
    'difficulty',
    'completed',
    'identified',
    'agreed',
    'willing',
    'participated',
    'dismissive',
    'defensive',
    'did well',
    'tolerated',
    'pushed back',
    'motivated',
    'declined',
    'refused',
    'ambivalent',
    'demonstrated',
    'showed',
    'expressed interest',
    'helpful',
    'benefited',
    'minimal engagement',
    'limited engagement',
    'took to',
  ],
  assessment: [
    'progress',
    'progressing',
    'improving',
    'improvement',
    'improved',
    'improve',
    'worsening',
    'worse',
    'decline',
    'declined',
    'stable',
    'consistent with',
    'continues to',
    'symptoms',
    'risk',
    'safety',
    'remains',
    'plateau',
    'fewer',
    'increased',
    'decreased',
    'reduction',
    'regressed',
    'appears to be',
    'seems to be',
    'insight',
    'judgment',
    'motivation',
    'better',
    'gains',
    'setback',
    'setbacks',
    'relapse',
    'no change',
    'unchanged',
    'ongoing',
    'persistent',
    'persists',
    'meets criteria',
    'clinically',
    'impression',
    'prognosis',
    'functioning',
    'coping well',
    'struggling',
    'likely',
    'suggests',
    'indicates',
    'less',
    'more',
  ],
  plan: [
    'next session',
    'next week',
    'homework',
    'will',
    'plan',
    'continue',
    'follow up',
    'follow-up',
    'referral',
    'refer',
    'schedule',
    'assign',
    'assigned',
    'between sessions',
    'by next',
    'coordinate',
    'check in',
    'check-in',
    'monitor',
    'going to',
    'revisit',
    'daily',
    'twice a day',
    'each day',
    'before next',
    'next',
    'practice',
    'until next',
    'recommend',
    'reassess',
    'target',
    'introduce',
    'begin',
    'start',
    'plan to',
    'agreed to',
  ],
};
const CUE_RES: Record<string, RegExp[]> = Object.fromEntries(
  Object.entries(NP_CUES).map(([k, cues]) => [
    k,
    cues.map((c) => new RegExp('\\b' + c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b')),
  ]),
);

// Early sentences lean subjective (what the client brought), late ones lean plan.
function positionBonus(key: string, i: number, n: number): number {
  if (n < 3) return 0;
  if (key === 'subjective' && i < n / 3) return 0.5;
  if (key === 'plan' && i >= (2 * n) / 3) return 0.5;
  return 0;
}

export function splitSentences(text: string): string[] {
  return (text || '')
    .replace(/[ \t]+/g, ' ')
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function score(sentence: string, key: string): number {
  const s = sentence.toLowerCase();
  return (CUE_RES[key] ?? []).reduce((n, re) => n + (re.test(s) ? 1 : 0), 0);
}

/** Clinician's first person -> note third person, capitalized, one closing period. */
export function clinicalize(sentence: string): string {
  let s = sentence
    .trim()
    .replace(/\bI\b/g, 'Clinician')
    .replace(/\bwe\b/gi, 'Clinician and client')
    .replace(/\bmy\b/gi, "clinician's")
    .replace(/\bme\b/gi, 'clinician')
    .replace(/\b(pt|patient)\b/gi, 'client');
  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (!/[.!?]$/.test(s)) s += '.';
  return s;
}

function pick(sentences: string[], key: string, max: number): string[] {
  const n = sentences.length;
  return sentences
    .map((text, i) => {
      const base = score(text, key);
      return { text, i, score: base > 0 ? base + positionBonus(key, i, n) : 0 };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .slice(0, max)
    .sort((a, b) => a.i - b.i)
    .map((x) => clinicalize(x.text));
}

export function npList(items: string[]): string {
  const arr = items.filter(Boolean);
  if (!arr.length) return '';
  if (arr.length === 1) return arr[0]!;
  return arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
}

const lower = (xs: string[]) => xs.map((c) => c.toLowerCase());

/** Summary text + structured note context -> a draft per section key. */
export function draftNoteSections(summary: string, ctx: NpContext, opts: NpOptions = {}): NpDrafts {
  if (opts.strict) return draftStrict(summary, ctx);
  const sentences = splitSentences(summary);
  const tagged = new Set<string>();
  const take = (key: string, max: number) => {
    const out = pick(sentences, key, max);
    out.forEach((t) => tagged.add(t));
    return out;
  };
  const subj = take('subjective', 2);
  const obj = take('objective', 2);
  const interv = take('intervention', 2);
  const resp = take('response', 2);
  const assess = take('assessment', 2);
  const plan = take('plan', 2);
  // Fallbacks from the summary itself, so a cue-free summary still lands in the right
  // places: early untagged sentences read as what the client brought, the closing
  // sentence reads as where things are headed, and response sentences double as
  // observable behavior. Nothing is invented.
  const n = sentences.length;
  const untagged = sentences
    .map((s, i) => ({ t: clinicalize(s), i }))
    .filter((x) => !tagged.has(x.t));
  if (!subj.length) {
    const early = untagged
      .filter((x) => n < 3 || x.i < (2 * n) / 3)
      .slice(0, 2)
      .map((x) => x.t);
    subj.push(...early);
    early.forEach((t) => tagged.add(t));
  }
  if (!plan.length && n > 1) {
    const last = untagged.filter((x) => x.i === n - 1 && !tagged.has(x.t)).map((x) => x.t);
    plan.push(...last);
    last.forEach((t) => tagged.add(t));
  }
  if (!obj.length) obj.push(...resp.slice(0, 1));
  if (!interv.length && untagged.length) {
    const mid = untagged
      .filter((x) => !tagged.has(x.t) && x.i > 0 && x.i < n - 1)
      .slice(0, 1)
      .map((x) => x.t);
    interv.push(...mid);
    mid.forEach((t) => tagged.add(t));
  }

  const goalLines = ctx.goals
    .filter((g) => g.checked)
    .slice(0, 2)
    .map(
      (g) =>
        `Progress toward "${g.goal}" (${g.objective}): ${String(g.progress || 'not rated').toLowerCase()}.`,
    );
  const dxLine = ctx.diagnoses.length
    ? `Presentation consistent with ${npList(ctx.diagnoses.map((d) => d.label || d.code))}.`
    : '';
  const sess = ctx.session || 'scheduled session';
  const attend = `Client attended ${/^[aeiou]/i.test(sess) ? 'an' : 'a'} ${sess}${ctx.response ? ' and was ' + ctx.response.toLowerCase() : ''}.`;
  const concernsLine = ctx.concerns.length
    ? `Client presented with ${npList(lower(ctx.concerns))}.`
    : '';
  const intervLine = ctx.interventions.length
    ? `Interventions used: ${npList(lower(ctx.interventions))}${ctx.approaches.length ? ' within ' + npList(ctx.approaches) : ''}.`
    : ctx.approaches.length
      ? `Session drew on ${npList(ctx.approaches)}.`
      : '';
  const respLine = ctx.response ? `Client response: ${ctx.response.toLowerCase()}.` : '';
  const stepsLine = ctx.nextSteps.length ? `Next steps: ${npList(lower(ctx.nextSteps))}.` : '';
  const approachLine = ctx.approaches.length ? `Continue ${npList(ctx.approaches)}.` : '';

  const join = (...parts: (string | string[] | undefined)[]) =>
    parts.flat().filter(Boolean).join(' ');
  const dataText = join(subj, obj, subj.length || obj.length ? '' : concernsLine, attend);
  return {
    subjective: join(subj, subj.length ? '' : concernsLine),
    objective: join(obj, attend),
    assessment: join(assess, goalLines, dxLine),
    plan: join(plan, stepsLine, plan.length ? '' : approachLine),
    data: dataText,
    behavior: dataText,
    intervention: join(interv, intervLine),
    response: join(resp, respLine, resp.length ? '' : goalLines[0]),
  };
}

// ── ehr#688 G5: strict drafting (group path) ────────────────────────────────
// GROUP_NOTE_10-29-V25 lineFor / shapeLine. Every line comes from a sentence in the notes or from
// a pick the clinician made on this note; a box with nothing stays empty, so the editor shows it as
// missing (red) and it counts in "N left to sign".

// V22 nextRe: a sentence about what comes next. Only these can go to Plan.
const NEXT_RE =
  /\bwill\b|\bplans? to\b|\bnext session\b|\bnext week\b|\bhomework\b|\bfollow[ -]up\b|\bschedul|\bcontinue\b|\bpractice\b(?!d)|\bgoing to\b/i;
// V23: a sentence that is plainly about what comes next goes to Plan only, never another box.
const FORWARD_LEAD = /^(plan|homework|next session|next week)\b/i;
const FORWARD_ANY = /\bwill\b|\bnext session\b|\bplans? to\b/i;
const isForward = (s: string) => FORWARD_LEAD.test(s.trim()) || FORWARD_ANY.test(s);
// V25 BOX_CUES: a group activity ("The group practiced grounding.") reads as an observation and as
// the client's part in it.
const STRICT_EXTRA: Record<string, RegExp[]> = {
  objective: [/\bpracticed\b/],
  response: [/\bpracticed\b/],
};
// V22 GROUP_SUBJ: a sentence about the whole group.
const GROUP_SUBJ =
  /^(the group|group members|the members|members|everyone|everybody|all members|they)\b/i;
const SAY = '(said|reported|stated|shared|mentioned|described|noted|told the group)';
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const lcFirst = (s: string) => (/^[A-Z][a-z]/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** The client's name as a pattern: "Devon", "Park" or "Devon Park" (case-sensitive). */
function namePattern(name: string | undefined): string | null {
  const words = (name ?? '')
    .split(/\s+/)
    .filter((w) => w.length > 1)
    .map(esc);
  return words.length ? `(?:${words.join('|')})(?:\\s+(?:${words.join('|')}))?` : null;
}

/** The clinician's first person in the third person; in a group "we" is the group. */
function groupVoice(sentence: string): string {
  let s = sentence
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^we\b/i, 'The group')
    .replace(/\bwe\b/gi, 'the group')
    .replace(/\bour\b/gi, "the group's")
    .replace(/\bI\b/g, 'Clinician')
    .replace(/\bmy\b/gi, "clinician's")
    .replace(/\bme\b/gi, 'clinician')
    .replace(/\b(pt|patient)\b/gi, 'client');
  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (!/[.!?]$/.test(s)) s += '.';
  return s;
}

/**
 * V22 shapeLine: a rough sentence as a readable line for one box. "Devon said work stress is
 * high." reads "Client reported work stress is high."; "The group practiced grounding." reads
 * "Client took part as the group practiced grounding." in Client response.
 */
export function shapeLine(key: string, sentence: string, clientName?: string): string {
  let t = groupVoice(sentence);
  const who = namePattern(clientName);
  const said =
    (who && new RegExp(`^${who}\\s+${SAY}\\s+(?:that\\s+)?(.+)$`).exec(t)) ||
    new RegExp(`^(?:she|he|they)\\s+${SAY}\\s+(?:that\\s+)?(.+)$`, 'i').exec(t);
  if (said) {
    const rest = lcFirst(said[2]!);
    return key === 'subjective' || key === 'data'
      ? `Client reported ${rest}`
      : `Client ${said[1]!.toLowerCase()} ${rest}`;
  }
  const named = who && new RegExp(`^${who}\\s+(.+)$`).exec(t);
  if (named) t = `Client ${named[1]}`;
  if (/^(client|clinician|the client)\b/i.test(t)) return t;
  // Assessment and Plan keep the sentence as written. (V25's "Assessment:" / "Plan:" prefix waits
  // on Jay, ehr#688 J4.)
  if (key === 'assessment' || key === 'plan') return t;
  if (GROUP_SUBJ.test(t)) {
    const lc = lcFirst(t);
    if (key === 'response' || key === 'behavior') return `Client took part as ${lc}`;
    if (key === 'objective') return `Clinician observed that ${lc}`;
    if (key === 'subjective' || key === 'data') return `Client reported that ${lc}`;
    return /^the group\b/i.test(t) ? t : `In group, ${lc}`;
  }
  return t;
}

function draftStrict(summary: string, ctx: NpContext): NpDrafts {
  const sentences = splitSentences(summary);
  const who = namePattern(ctx.clientName);
  const ownRe = who ? new RegExp(`\\b${who}\\b`) : null;
  const aboutClient = (s: string) => !!ownRe?.test(s) || /^(she|he)\b/i.test(s.trim());
  const aboutGroup = (s: string) => GROUP_SUBJ.test(groupVoice(s));
  const pickStrict = (key: string): string[] => {
    let found = sentences
      .map((text, i) => {
        let n = score(text, key);
        n += (STRICT_EXTRA[key] ?? []).filter((re) => re.test(text.toLowerCase())).length;
        if (key === 'plan') n = NEXT_RE.test(text) || isForward(text) ? n + 1 : 0;
        else if (isForward(text)) n = 0;
        if (n > 0 && aboutClient(text)) n += 0.5;
        return { text, i, n };
      })
      .filter((x) => x.n > 0);
    // V22: a line about this client beats a line about everyone.
    if (found.some((x) => aboutClient(x.text))) found = found.filter((x) => !aboutGroup(x.text));
    return found
      .sort((a, b) => b.n - a.n || a.i - b.i)
      .slice(0, 2)
      .sort((a, b) => a.i - b.i)
      .map((x) => x.text);
  };
  const subj = pickStrict('subjective');
  const obj = pickStrict('objective');
  const interv = pickStrict('intervention');
  const resp = pickStrict('response');
  const assess = pickStrict('assessment');
  const plan = pickStrict('plan');
  const as = (key: string, xs: string[]) => xs.map((s) => shapeLine(key, s, ctx.clientName));

  // Lines from picks the clinician made on this note (Credify Note chips, goals) stay; nothing
  // from the session length, the diagnoses or the group's approaches alone.
  const goalLines = ctx.goals
    .filter((g) => g.checked)
    .slice(0, 2)
    .map(
      (g) =>
        `Progress toward "${g.goal}" (${g.objective}): ${String(g.progress || 'not rated').toLowerCase()}.`,
    );
  const concernsLine = ctx.concerns.length
    ? `Client presented with ${npList(lower(ctx.concerns))}.`
    : '';
  const intervLine = ctx.interventions.length
    ? `Interventions used: ${npList(lower(ctx.interventions))}${ctx.approaches.length ? ' within ' + npList(ctx.approaches) : ''}.`
    : '';
  const respLine = ctx.response ? `Client response: ${ctx.response.toLowerCase()}.` : '';
  const stepsLine = ctx.nextSteps.length ? `Next steps: ${npList(lower(ctx.nextSteps))}.` : '';

  const join = (...parts: (string | string[])[]) => parts.flat().filter(Boolean).join(' ');
  const none = !subj.length && !obj.length;
  return {
    subjective: join(as('subjective', subj), subj.length ? '' : concernsLine),
    objective: join(as('objective', obj)),
    assessment: join(as('assessment', assess), goalLines),
    plan: join(as('plan', plan), stepsLine),
    data: join(as('data', subj), as('objective', obj), none ? concernsLine : ''),
    behavior: join(as('subjective', subj), as('behavior', obj), none ? concernsLine : ''),
    intervention: join(as('intervention', interv), intervLine),
    response: join(as('response', resp), respLine),
  };
}

/**
 * Merge policy: fill empty fields, refresh fields NotePilot drafted, never touch
 * clinician text. Returns the new text for each field it wrote plus a tally.
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V83.5): pass an empty `drafted` set to fill only the empty
 * boxes (AI text nobody reviewed is left alone too). `skipped` names the boxes left alone.
 */
export function mergeDrafts(
  drafts: Partial<NpDrafts>,
  fields: string[],
  current: Record<string, string>,
  drafted: ReadonlySet<string>,
): {
  writes: Record<string, string>;
  drafted: number;
  kept: number;
  skipped: string[];
  empty: string[];
} {
  const writes: Record<string, string> = {};
  let n = 0;
  let kept = 0;
  const skipped: string[] = [];
  const empty: string[] = [];
  for (const key of fields) {
    const have = (current[key] ?? '').trim();
    if (have && !drafted.has(key)) {
      kept++;
      skipped.push(key);
      continue;
    }
    const text = (drafts[key as keyof NpDrafts] ?? '').trim();
    if (!text) {
      if (!have) empty.push(key);
      continue;
    }
    writes[key] = text;
    n++;
  }
  return { writes, drafted: n, kept, skipped, empty };
}

// ── Built-in restyle: word swaps with case kept, plus detail from the note ──
type Pair = [RegExp, string];
function swap(text: string, pairs: Pair[]): string {
  return pairs.reduce(
    (t, [re, repl]) =>
      t.replace(re, (m) => {
        const c = m.charAt(0);
        const cap = c === c.toUpperCase() && c !== c.toLowerCase();
        return cap ? repl.charAt(0).toUpperCase() + repl.slice(1) : repl;
      }),
    text,
  );
}
const CONTRACTIONS: Pair[] = [
  [/\bcan't\b/gi, 'cannot'],
  [/\bwon't\b/gi, 'will not'],
  [/\bdon't\b/gi, 'do not'],
  [/\bdoesn't\b/gi, 'does not'],
  [/\bdidn't\b/gi, 'did not'],
  [/\bisn't\b/gi, 'is not'],
  [/\baren't\b/gi, 'are not'],
  [/\bwasn't\b/gi, 'was not'],
  [/\bweren't\b/gi, 'were not'],
  [/\bhasn't\b/gi, 'has not'],
  [/\bhaven't\b/gi, 'have not'],
  [/\bhadn't\b/gi, 'had not'],
  [/\bcouldn't\b/gi, 'could not'],
  [/\bshouldn't\b/gi, 'should not'],
  [/\bwouldn't\b/gi, 'would not'],
  [/\bit's\b/gi, 'it is'],
  [/\bshe's\b/gi, 'she is'],
  [/\bhe's\b/gi, 'he is'],
  [/\bthey're\b/gi, 'they are'],
  [/\bthat's\b/gi, 'that is'],
  [/\bthere's\b/gi, 'there is'],
  [/\bwe're\b/gi, 'we are'],
  [/\byou're\b/gi, 'you are'],
  [/\bI'm\b/g, 'I am'],
  [/\bI've\b/g, 'I have'],
  [/\bwe've\b/gi, 'we have'],
  [/\bthey've\b/gi, 'they have'],
  [/\bI'll\b/g, 'I will'],
  [/\bshe'll\b/gi, 'she will'],
  [/\bhe'll\b/gi, 'he will'],
  [/\bwe'll\b/gi, 'we will'],
  [/\bthey'll\b/gi, 'they will'],
  [/\bI'd\b/g, 'I would'],
  [/\bshe'd\b/gi, 'she would'],
  [/\bhe'd\b/gi, 'he would'],
];
const FORMAL: Pair[] = [
  [/\bkids\b/gi, 'children'],
  [/\bkid\b/gi, 'child'],
  [/\bmom\b/gi, 'mother'],
  [/\bdad\b/gi, 'father'],
  [/\ba lot\b/gi, 'considerably'],
  [/\blots of\b/gi, 'many'],
  [/\breally\b/gi, 'notably'],
  [/\bpretty\b/gi, 'fairly'],
  [/\bgot\b/gi, 'received'],
  [/\bokay\b/gi, 'acceptable'],
  [/\bok\b/gi, 'acceptable'],
  [/\bstuff\b/gi, 'matters'],
  [/\bbig\b/gi, 'significant'],
  [/\bhuge\b/gi, 'substantial'],
  [/\bhelped\b/gi, 'was beneficial'],
  [/\btalked about\b/gi, 'discussed'],
  [/\btalk about\b/gi, 'discuss'],
  [/\bthinks\b/gi, 'believes'],
  [/\bwants\b/gi, 'wishes'],
  [/\bseems\b/gi, 'appears'],
  [/\bseemed\b/gi, 'appeared'],
  [/\bdid well\b/gi, 'performed well'],
  [/\bwent well\b/gi, 'proceeded well'],
  [/\ba bit\b/gi, 'somewhat'],
  [/\bkind of\b/gi, 'somewhat'],
  [/\bsort of\b/gi, 'somewhat'],
  [/\bmaybe\b/gi, 'perhaps'],
  [/\bgonna\b/gi, 'going to'],
  [/\bwanna\b/gi, 'want to'],
  [/\byeah\b/gi, 'yes'],
];
const CLINICAL: Pair[] = [
  [/\bsaid\b/gi, 'reported'],
  [/\bsays\b/gi, 'reports'],
  [/\btold\b/gi, 'reported to'],
  [/\bshared\b/gi, 'disclosed'],
  [/\bmentioned\b/gi, 'reported'],
  [/\bfeels\b/gi, 'endorses feeling'],
  [/\bfelt\b/gi, 'endorsed feeling'],
  [/\bthinks\b/gi, 'believes'],
  [/\bworried\b/gi, 'endorsed worry'],
  [/\bhelped\b/gi, 'was effective'],
  [/\bsad\b/gi, 'low in mood'],
  [/\bnervous\b/gi, 'anxious'],
  [/\bangry\b/gi, 'irritable'],
  [/\bmad\b/gi, 'irritable'],
  [/\btired\b/gi, 'fatigued'],
  [/\bexhausted\b/gi, 'fatigued'],
  [/\bsleep problems\b/gi, 'sleep disturbance'],
  [/\bcan't sleep\b/gi, 'reports insomnia'],
  [/\bcouldn't sleep\b/gi, 'reported insomnia'],
  [/\btalked about\b/gi, 'processed'],
  [/\bwent over\b/gi, 'reviewed'],
  [/\bhomework\b/gi, 'between-session assignment'],
  [/\bimproving\b/gi, 'demonstrating improvement'],
  [/\bbetter\b/gi, 'improved'],
  [/\bworse\b/gi, 'exacerbated'],
  [/\ba lot\b/gi, 'significantly'],
  [/\bupset\b/gi, 'distressed'],
  [/\bpracticed\b/gi, 'rehearsed'],
  [/\btried\b/gi, 'attempted'],
  [/\bcried\b/gi, 'was tearful'],
  [/\bcrying\b/gi, 'tearful'],
  [/\bscared\b/gi, 'fearful'],
  [/\bafraid\b/gi, 'fearful'],
  [/\bstressed\b/gi, 'reported elevated stress'],
  [/\bdrinking\b/gi, 'alcohol use'],
  [/\bdrugs\b/gi, 'substance use'],
  [/\bThe client\b/g, 'Client'],
  [/\bThe clinician\b/g, 'Clinician'],
];
// Only bare abbreviations expand; "(CBT)" after an expansion is left alone.
const ABBREV: Pair[] = [
  [/(?<!\()\bCBT\b/g, 'cognitive behavioral therapy (CBT)'],
  [/(?<!\()\bDBT\b/g, 'dialectical behavior therapy (DBT)'],
  [/(?<!\()\bEMDR\b/g, 'eye movement desensitization and reprocessing (EMDR)'],
  [/(?<!\()\bERP\b/g, 'exposure and response prevention (ERP)'],
  [/(?<!\()\bPTSD\b/g, 'post-traumatic stress disorder (PTSD)'],
];

/** Extra sentences per field for In-depth, all from the structured fields on the note. */
export function detailLines(field: string, ctx: NpContext): string[] {
  const checked = ctx.goals.filter((g) => g.checked);
  const goalLines = checked.map(
    (g) =>
      `Progress toward "${g.goal}" (${g.objective}): ${String(g.progress || 'not rated').toLowerCase()}.`,
  );
  const ongoing = checked
    .filter((g) => /no change|worsened/i.test(g.progress || ''))
    .map((g) => `Continue targeting: ${g.objective}.`);
  const concerns = ctx.concerns.length
    ? `Presenting concerns this session: ${npList(lower(ctx.concerns))}.`
    : '';
  const dx = ctx.diagnoses.length
    ? `Working diagnosis: ${npList(ctx.diagnoses.map((d) => (d.code + ' ' + d.label).trim()))}.`
    : '';
  const approaches = ctx.approaches.length ? `Treatment approach: ${npList(ctx.approaches)}.` : '';
  const interventions = ctx.interventions.length
    ? `Interventions used: ${npList(lower(ctx.interventions))}.`
    : '';
  const response = ctx.response ? `Client engagement: ${ctx.response.toLowerCase()}.` : '';
  const steps = ctx.nextSteps.length ? `Agreed next steps: ${npList(lower(ctx.nextSteps))}.` : '';
  const session = ctx.session ? `Session format: ${ctx.session.replace(/ session$/, '')}.` : '';
  const counts: Record<string, number> = {};
  for (const g of checked) {
    const k = String(g.progress || '').toLowerCase();
    counts[k] = (counts[k] ?? 0) + 1;
  }
  const imp = counts.improved ?? 0;
  const none = counts['no change'] ?? 0;
  const worse = counts.worsened ?? 0;
  const overall = checked.length
    ? `Overall, progress toward treatment goals is ${imp > none + worse ? 'improving' : worse > imp ? 'showing setbacks' : 'mixed'}.`
    : '';
  switch (field) {
    case 'subjective':
    case 'data':
    case 'behavior':
      return [
        concerns,
        session,
        checked.length ? `Goals addressed: ${npList(checked.map((g) => g.goal))}.` : '',
      ];
    case 'objective':
      return [session, response, approaches];
    case 'assessment':
      return [...goalLines, overall, dx, approaches];
    case 'plan':
      return [
        steps,
        ...ongoing,
        ctx.approaches.length ? `Continue ${npList(ctx.approaches)} in upcoming sessions.` : '',
        'Review progress at the next scheduled session.',
      ];
    case 'intervention':
      return [
        interventions,
        approaches,
        ctx.concerns.length ? `Interventions targeted ${npList(lower(ctx.concerns))}.` : '',
      ];
    case 'response':
      return [response, ...goalLines];
    default:
      return [];
  }
}

/** Text + tone + field + note context -> restyled text. */
export function restyleText(text: string, tone: NpTone, field: string, ctx: NpContext): string {
  let out = swap(text.trim(), CONTRACTIONS);
  if (tone === 'formal') {
    out = swap(out, FORMAL)
      .replace(/(^|[.!?]\s+)Client\b/g, '$1The client')
      .replace(/(^|[.!?]\s+)Clinician\b/g, '$1The clinician');
  } else if (tone === 'clinical') {
    out = swap(out, CLINICAL);
  } else {
    out = ABBREV.reduce((t, [re, repl]) => t.replace(re, repl), out);
    const have = out.toLowerCase();
    const extra = detailLines(field, ctx).filter(
      (l) => l && !have.includes(l.toLowerCase().slice(0, 40)),
    );
    out = [out, ...extra].join(' ');
  }
  return out;
}

// ── Session summary (limited rich text) ─────────────────────────────────────
const SUMMARY_TAGS = new Set(['DIV', 'P', 'BR', 'UL', 'OL', 'LI']);

/**
 * Keep only the list/paragraph structure the toolbar makes (div, p, br, ul, ol, li),
 * with every attribute dropped, before stored summary HTML goes back into the editor.
 */
export function sanitizeSummaryHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const clean = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent ?? '').replace(
        /[&<>]/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] ?? c,
      );
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as Element;
    const inner = Array.from(el.childNodes).map(clean).join('');
    if (!SUMMARY_TAGS.has(el.tagName)) return inner;
    const tag = el.tagName.toLowerCase();
    return tag === 'br' ? '<br>' : `<${tag}>${inner}</${tag}>`;
  };
  const root = doc.body.firstElementChild;
  return root ? Array.from(root.childNodes).map(clean).join('') : '';
}

// ── ehr#478 (V81 Draft with AI): the rough notes ────────────────────────────
// The rough notes are plain text in a box. They are kept where the session summary was
// (body.sessionSummary, its limited HTML), so notes written before read the same in print.
const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};
/** Stored summary HTML as plain text: one line per line, list item ("- ") or paragraph. */
export function summaryToText(html: string): string {
  if (!html) return '';
  // each open list: numbered (its count so far) or bulleted (null)
  const lists: (number | null)[] = [];
  let out = '';
  for (const part of html.split(/(<[^>]*>)/)) {
    const tag = /^<\s*(\/?)\s*([a-z0-9]+)/i.exec(part);
    if (!tag) {
      out += part.replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (m) => ENTITIES[m] ?? m);
      continue;
    }
    const close = tag[1] === '/';
    const name = tag[2]!.toLowerCase();
    if (name === 'ol' || name === 'ul') {
      if (close) lists.pop();
      else lists.push(name === 'ol' ? 0 : null);
      out += '\n';
    } else if (name === 'li' && !close) {
      const top = lists.length - 1;
      const n = lists[top];
      if (typeof n === 'number') lists[top] = n + 1;
      out += typeof n === 'number' ? `\n${n + 1}. ` : '\n- ';
    } else if (name === 'br' || (close && (name === 'p' || name === 'div'))) out += '\n';
  }
  return out
    .split('\n')
    .map((l) => l.replace(/\s+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
/** Plain text as summary HTML: escaped, a <br> for each line break. */
export function textToSummary(text: string): string {
  return text
    .trim()
    .replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] ?? c)
    .replace(/\r?\n/g, '<br>');
}

/**
 * ehr#571 (case management round, Notes Drawer V20 serviceDraft, 13 A): a Service note (case
 * management, peer support, skills / rehab, collateral contact) with no rough notes drafts only
 * facts from the chart. What happened: the kind, where and when. Next step: the last signed note of
 * the same kind's Next step, else a follow-up on the plan's first goal, else on the plan.
 */
export function serviceDraft(x: {
  kind: string;
  place: string;
  /** "3:00 PM", '' when not set. */
  start: string;
  end: string;
  minutes: number | null;
  lastNext?: string;
  goal?: string;
}): { subjective: string; plan: string } {
  const when =
    x.start && x.end && x.minutes != null && x.minutes > 0
      ? `${x.start} to ${x.end} (${x.minutes} min)`
      : x.start
        ? `started ${x.start}`
        : '';
  const next = (x.lastNext ?? '').trim();
  const goal = (x.goal ?? '').trim();
  // ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V8 fix): "Case management contact, in person, ..."
  const place = x.kind === 'Case management' ? x.place.toLowerCase() : x.place;
  // ehr#728 (COLLATERAL_CONTACT_NOTE V2): "Collateral contact, ...", never "Collateral contact contact"
  const what = /contact$/i.test(x.kind) ? x.kind : `${x.kind} contact`;
  return {
    subjective: `${what}, ${place}${when ? `, ${when}` : ''}.`,
    plan:
      next ||
      (goal ? `Next contact: follow up on "${goal}".` : 'Next contact: follow up on the plan.'),
  };
}
